import { Auditor, EventAuditor, OrganizationAuditor } from "../../models";
import { AuditorStatus, UpdateAuditorInput } from "../../API";
import { deleteAuditor, updateAuditor } from "./Auditor";
import { getEventAuditorsByPhoneNumberSearch, updateEventAuditor } from "../eventAuditor/EventAuditor";
import { getOrganizationAuditorByPhoneNumber, getOrganizationAuditorsByPhoneNumberSearch, updateOrganizationAuditor } from "../organizationAuditor/OrganizationAuditor";
import { Result } from "../../interfaces/Result";
import formatResult from "../ReturnType/ReturnType";

export interface MergeAuditorsInput {
    primaryAuditor: Auditor;
    duplicateAuditors: Auditor[];
}

/**
 * Merge duplicate auditor records into a single primary record
 * @param input - Contains the primary auditor and array of duplicates to merge
 * @returns Result indicating success or failure
 */
export async function mergeAuditors(input: MergeAuditorsInput): Promise<Result> {
    const { primaryAuditor, duplicateAuditors } = input;

    if (!primaryAuditor?.id) {
        return formatResult(false, "Auditor", null, "Primary auditor is required");
    }

    if (!duplicateAuditors || duplicateAuditors.length === 0) {
        return formatResult(false, "Auditor", null, "No duplicate auditors to merge");
    }

    try {
        // Step 1: Update primary auditor with any missing data from duplicates
        const updatedPrimaryAuditor = await updatePrimaryAuditorWithMissingData(primaryAuditor, duplicateAuditors);
        
        if (!updatedPrimaryAuditor.isSuccess) {
            return formatResult(false, "Auditor", null, "Failed to update primary auditor with missing data");
        }

        // Step 2: Migrate all EventAuditor records from duplicates to primary
        for (const duplicate of duplicateAuditors) {
            const eventAuditorsResult = await migrateEventAuditors(duplicate, primaryAuditor);
            if (!eventAuditorsResult.isSuccess) {
                console.error(`Failed to migrate EventAuditors from ${duplicate.id}:`, eventAuditorsResult.message);
                // Continue with other duplicates even if one fails
            }
        }

        // Step 3: Migrate all OrganizationAuditor records from duplicates to primary
        for (const duplicate of duplicateAuditors) {
            const orgAuditorsResult = await migrateOrganizationAuditors(duplicate, primaryAuditor);
            if (!orgAuditorsResult.isSuccess) {
                console.error(`Failed to migrate OrganizationAuditors from ${duplicate.id}:`, orgAuditorsResult.message);
                // Continue with other duplicates even if one fails
            }
        }

        // Step 4: Delete all duplicate auditor records
        for (const duplicate of duplicateAuditors) {
            const deleteResult = await deleteAuditor({id: duplicate.id});
            if (!deleteResult.isSuccess) {
                console.error(`Failed to delete duplicate auditor ${duplicate.id}:`, deleteResult.message);
                // Continue with other duplicates even if one fails
            }
        }

        return formatResult(
            true, 
            "Auditor", 
            { primaryAuditor: updatedPrimaryAuditor.result, mergedCount: duplicateAuditors.length }, 
            `Successfully merged ${duplicateAuditors.length} duplicate auditor(s) into primary record`
        );

    } catch (error: any) {
        console.error("Error merging auditors:", error);
        return formatResult(false, "Auditor", error, "An error occurred while merging auditors");
    }
}

/**
 * Update primary auditor with any missing data from duplicate records
 */
async function updatePrimaryAuditorWithMissingData(
    primaryAuditor: Auditor, 
    duplicates: Auditor[]
): Promise<Result> {
    const updateInput: UpdateAuditorInput = {
        id: primaryAuditor.id
    };

    let hasUpdates = false;

    // Check each duplicate for values that primary is missing
    for (const duplicate of duplicates) {
        if (!primaryAuditor.personId && duplicate.personId) {
            updateInput.personId = duplicate.personId;
            hasUpdates = true;
        }
        if (!primaryAuditor.name && duplicate.name) {
            updateInput.name = duplicate.name;
            hasUpdates = true;
        }
        if (!primaryAuditor.email && duplicate.email) {
            updateInput.email = duplicate.email;
            hasUpdates = true;
        }
        if (!primaryAuditor.status && duplicate.status) {
            updateInput.status = duplicate.status as AuditorStatus;
            hasUpdates = true;
        }
        if (!primaryAuditor.statusNote && duplicate.statusNote) {
            updateInput.statusNote = duplicate.statusNote;
            hasUpdates = true;
        }
        if (!primaryAuditor.createdBy && duplicate.createdBy) {
            updateInput.createdBy = duplicate.createdBy;
            hasUpdates = true;
        }
    }

    // Only update if there are changes
    if (hasUpdates) {
        return await updateAuditor(updateInput);
    }

    // No updates needed, return success with original auditor
    return formatResult(true, "Auditor", primaryAuditor, "No updates needed for primary auditor");
}

/**
 * Migrate all EventAuditor records from one auditor to another
 */
async function migrateEventAuditors(fromAuditor: Auditor, toAuditor: Auditor): Promise<Result> {
    try {

        if (!fromAuditor.phoneNumber) return formatResult(false, "EventAuditor", "Auditor does not have a phone number.", "Error migrating EventAuditors");

        // Get all EventAuditor records for the duplicate auditor
        const eventAuditorsResult = await getEventAuditorsByPhoneNumberSearch(fromAuditor.phoneNumber);
        
        if (!eventAuditorsResult.isSuccess) {
            return eventAuditorsResult;
        }

        const eventAuditors: EventAuditor[] = eventAuditorsResult.result || [];

        // Update each EventAuditor to point to the primary auditor
        for (const eventAuditor of eventAuditors) {
            if (eventAuditor.auditorId !== toAuditor.id) {
                const updateResult = await updateEventAuditor({
                    id: eventAuditor.id,
                    auditorId: toAuditor.id
                });

                if (!updateResult.isSuccess) {
                    console.error(`Failed to update EventAuditor ${eventAuditor.id}:`, updateResult.message);
                }
            }
        }

        return formatResult(
            true, 
            "EventAuditor", 
            eventAuditors, 
            `Migrated ${eventAuditors.length} EventAuditor record(s)`
        );

    } catch (error: any) {
        return formatResult(false, "EventAuditor", error, "Error migrating EventAuditors");
    }
}

/**
 * Migrate all OrganizationAuditor records from one auditor to another
 */
async function migrateOrganizationAuditors(fromAuditor: Auditor, toAuditor: Auditor): Promise<Result> {
    try {

        if (!fromAuditor.phoneNumber) return formatResult(false, "OrgnizationAuditor", "Auditor does not have a phone number.", "Error migrating OrgnizationAuditors");

        // Get all OrganizationAuditor records for the duplicate auditor
        const orgAuditorsResult = await getOrganizationAuditorsByPhoneNumberSearch(fromAuditor.phoneNumber);
        
        if (!orgAuditorsResult.isSuccess) {
            return orgAuditorsResult;
        }

        const orgAuditors: OrganizationAuditor[] = orgAuditorsResult.result || [];

        // Update each OrganizationAuditor to point to the primary auditor
        for (const orgAuditor of orgAuditors) {
            const updateResult = await updateOrganizationAuditor({
                id: orgAuditor.id,
                auditorId: toAuditor.id
            });

            if (!updateResult.isSuccess) {
                console.error(`Failed to update OrganizationAuditor ${orgAuditor.id}:`, updateResult.message);
            }
        }

        return formatResult(
            true, 
            "OrganizationAuditor", 
            orgAuditors, 
            `Migrated ${orgAuditors.length} OrganizationAuditor record(s)`
        );

    } catch (error: any) {
        return formatResult(false, "OrganizationAuditor", error, "Error migrating OrganizationAuditors");
    }
}
